import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Skeleton
} from "@mui/material";
import { CheckCircle, Delete, Info } from "@mui/icons-material";
import {
    idGrupoPlanificacionOptions,
    lineaKUTOptions,
    grupoAlmacenamientoOptions,
    fetchCategoryOptions,
    fetchBaseMeasureUnitCodeOptions,
    fetchValoracionCodeOptions
} from '../../constantes/constants';

const ConfirmMaterials = ({ materials, sameProvider, sameCategoria, sameGrupoDePlanificacion, handleDelete }) => {
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [baseUnitOptions, setBaseUnitOptions] = useState([]);
    const [valoracionOptions, setValoracionOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const [cats, units, vals] = await Promise.all([
                    fetchCategoryOptions(),
                    fetchBaseMeasureUnitCodeOptions(),
                    fetchValoracionCodeOptions()
                ]);
                setCategoryOptions(cats);
                setBaseUnitOptions(units);
                setValoracionOptions(vals);
            } catch (e) {
                console.error("Error cargando opciones en confirmación:", e);
            } finally {
                setLoadingOptions(false);
            }
        };
        load();
    }, []);

    const getLabel = (options, value, labelKey = 'label', valueKey = 'value') => {
        if (!value) return '-';
        const found = options.find(o => o[valueKey] === value);
        return found ? found[labelKey] : value;
    };

    const getCategoryLabel = (value) => getLabel(categoryOptions, value);
    const getBaseUnitLabel = (value) => getLabel(baseUnitOptions, value, 'nombre');
    const getValoracionLabel = (value) => getLabel(valoracionOptions, value);
    const getLineaKUTLabel = (value) => getLabel(lineaKUTOptions, value);
    const getGrupoLabel = (value) => getLabel(idGrupoPlanificacionOptions, value);
    const getAlmacenamientoLabel = (value) => getLabel(grupoAlmacenamientoOptions, value, 'nombre', 'codigo');

    const renderStatusIcon = (condition) =>
        condition ? <CheckCircle color="success" /> : <Info color="info" />;

    const renderCell = (content) => {
        if (loadingOptions) return <Skeleton width={80} />;
        return content || '-';
    };

    return (
        <Box>
            {/* Resumen de configuración */}
            <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 4, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                    Resumen de Configuración
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {renderStatusIcon(sameProvider)}
                            <Typography variant="body2">
                                {sameProvider ? "Proveedor unificado" : "Proveedores individuales"}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {renderStatusIcon(sameCategoria)}
                            <Typography variant="body2">
                                {sameCategoria ? "Categoría unificada" : "Categorías individuales"}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {renderStatusIcon(sameGrupoDePlanificacion)}
                            <Typography variant="body2">
                                {sameGrupoDePlanificacion ? "Planificación unificada" : "Planificación individual"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Tabla de materiales */}
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'primary.main' }}>
                        <TableRow>
                            {[
                                'Nombre', 'Categoría', 'Und. Medida', 'Línea',
                                'Comentario Venta', 'Grupo Planificación', 'Descripción',
                                'Proveedor', 'Parte #', 'Almacenamiento', 'Valoración', 'Acciones'
                            ].map((head) => (
                                <TableCell key={head} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                    {head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {materials.map((material) => (
                            <TableRow key={material.id} hover>
                                <TableCell sx={{ fontWeight: 'medium', minWidth: 140 }}>
                                    {material.nombreItem}
                                </TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                    {renderCell(getCategoryLabel(material.categoriaProducto))}
                                </TableCell>
                                <TableCell sx={{ minWidth: 120 }}>
                                    {renderCell(getBaseUnitLabel(material.baseMeasureUnitCode))}
                                </TableCell>
                                <TableCell sx={{ minWidth: 120 }}>
                                    {getLineaKUTLabel(material.lineaKUT)}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 120 }}>
                                    {material.comentarioLineasDeVenta}
                                </TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                    {getGrupoLabel(material.idGrupoPlanificacion)}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 120 }}>
                                    {material.descripcionMaterial}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 130 }}>
                                    {material.proveedor ? material.proveedor.split('|')[0] : '-'}
                                </TableCell>
                                <TableCell>{material.codigoProveedor || '-'}</TableCell>
                                <TableCell sx={{ minWidth: 140 }}>
                                    {getAlmacenamientoLabel(material.grupoAlmacenamiento)}
                                </TableCell>
                                <TableCell sx={{ minWidth: 120 }}>
                                    {renderCell(getValoracionLabel(material.valoracionProducto))}
                                </TableCell>
                                <TableCell>
                                    <IconButton color="error" size="small" onClick={() => handleDelete(material.id)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ConfirmMaterials;
